import React, { useContext, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  Form,
  Field,
  Actions,
  Loading,
  Pagetitle,
  FriendlyCaptcha,
} from '@ktag/styleguide/dist/react';
import { useTranslation } from 'react-i18next';
import { useMutation } from '@tanstack/react-query';
import { RequestProfileRequest } from '@ktag/newsletters-sdk/dist';
import AppContext from 'AppContext';
import DataPolicy from 'components/dataPolicy/DataPolicy';
import BackToOverview from 'components/backToOverview/BackToOverview';
import { EmailFormMode } from 'enums/emailFormModes';
import { EMAIL_PATTERN } from 'utils/emailValidationPattern';
import EmailFormProgress from './EmailFormProgress';
import EmailFormInfoBox from './EmailFormInfoBox';

const MODE_EDIT: EmailFormMode = EmailFormMode.EditMode;

interface Props {
  mode: string;
}

type FormValues = {
  email: string;
  friendlyCaptcha?: string;
};

const EmailForm = ({ mode }: Props) => {
  const { t } = useTranslation('newsletter');
  const { id } = useParams<{ id: string }>();
  const { profileApi, cancellationApi, siteKey } = useContext(AppContext);
  const [emailFormEditStep, setEmailFormEditStep] = useState(1);
  const [emailFormUnsubscribeStep, setEmailFormUnsubscribeStep] = useState(1);

  const requestProfile = useMutation<void, void, RequestProfileRequest>({
    mutationFn: (requestProfileRequestData) =>
      profileApi.requestProfile(requestProfileRequestData),
  });

  const requestCancellation = useMutation<void, void, RequestProfileRequest>({
    mutationFn: (requestCancellationRequest) =>
      cancellationApi.requestCancellation(Number(id), requestCancellationRequest),
  });

  const handleRequestProfileSubmit = async ({ email, friendlyCaptcha }: FormValues) => {
    const requestProfileRequest: RequestProfileRequest = {
      email,
      captcha: friendlyCaptcha,
    };

    requestProfile.mutate(requestProfileRequest);
    setEmailFormEditStep(2);
  };

  const handleRequestUnsubscibeSubmit = async ({
    email,
    friendlyCaptcha,
  }: FormValues) => {
    const requestCancellationRequest: RequestProfileRequest = {
      email,
      captcha: friendlyCaptcha,
    };

    requestCancellation.mutate(requestCancellationRequest);
    setEmailFormUnsubscribeStep(2);
  };

  return (
    <div className="emailform richtext">
      <Pagetitle additionalClassNames="emailform__header">
        {mode === MODE_EDIT ? t('edit_header') : t('unsubscribe_header')}
      </Pagetitle>
      <EmailFormProgress
        isSuccess={requestProfile.isSuccess || requestCancellation.isSuccess}
        mode={mode}
      />
      {emailFormEditStep === 1 && emailFormUnsubscribeStep === 1 && (
        <Form
          onSubmit={
            mode === MODE_EDIT
              ? handleRequestProfileSubmit
              : handleRequestUnsubscibeSubmit
          }
        >
          <Field
            label={t('newsletter_form_email')}
            name="email"
            required
            type="email"
            pattern={EMAIL_PATTERN}
            patternErrorMessage={t('newsletter_email_form_pattern_error')}
          />

          <p className="emailform__required-text">{t('emailform_required_field_text')}</p>

          <div className="emailform__captcha-container" id="dada">
            <FriendlyCaptcha siteKey={siteKey} />
          </div>

          <div className="emailform__footer-container">
            <DataPolicy />
            <Actions
              label={mode === MODE_EDIT ? t('edit_edit') : t('unsubscribe_unsubscribe')}
            />
          </div>
        </Form>
      )}

      {(requestProfile.isLoading || requestCancellation.isLoading) && (
        <Loading size="medium" />
      )}

      {!requestProfile.isLoading && emailFormEditStep === 2 && (
        <EmailFormInfoBox mode={mode} isSuccess={requestProfile.isSuccess} />
      )}

      {!requestCancellation.isLoading && emailFormUnsubscribeStep === 2 && (
        <EmailFormInfoBox mode={mode} isSuccess={requestCancellation.isSuccess} />
      )}

      {(emailFormEditStep === 2 || emailFormUnsubscribeStep === 2) && (
        <div className="emailform__confirmation-footer-container">
          <DataPolicy />
          <BackToOverview />
        </div>
      )}
    </div>
  );
};

export default EmailForm;
